import { Link } from "react-router-dom";
import { ArrowRight, Droplets, Layers, ShieldCheck, Hammer, Grid3x3, Sprout, Trees, Ruler, Wrench, Quote } from "lucide-react";
import Reveal from "@/components/Reveal";
import Stars from "@/components/Stars";
import GoogleReviewsPanel from "@/components/GoogleReviewsPanel";
import useSEO from "@/hooks/useSEO";
import { HERO_IMAGE, WHY_IMAGE, SERVICES, TESTIMONIALS } from "@/data/site";

const FEATURE_CARDS = [
  { title: "Drainage Done Right", text: "Engineered solutions that protect your property for the long term — no guesswork, no shortcuts.", icon: Droplets },
  { title: "Professional & Organised", text: "Clear scopes, tidy sites, and predictable timelines. We treat your home like our own.", icon: ShieldCheck },
  { title: "No Cowboys, Just Craftsmanship", text: "A small, skilled team of lifelong tradesmen — fully accountable, end to end.", icon: Hammer },
];

const ICONS = { Layers, Droplets, Grid3x3, Fence: Wrench, Ruler, Sprout, Trees };

export default function Home() {
  useSEO();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#050505] min-h-[90vh] md:min-h-[88vh] flex items-center">
        {/* Background image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
          data-testid="hero-bg"
        />
        {/* Dark overlay for legibility */}
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(5,5,5,0.92) 0%, rgba(5,5,5,0.78) 45%, rgba(5,5,5,0.55) 75%, rgba(5,5,5,0.35) 100%), linear-gradient(180deg, rgba(5,5,5,0.45) 0%, rgba(5,5,5,0.1) 50%, rgba(5,5,5,0.65) 100%)",
          }}
          aria-hidden="true"
        />

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 w-full">
          <div className="max-w-3xl">
            <Reveal>
              <p className="cc-eyebrow mb-6">Construction & Landscaping</p>
            </Reveal>
            <Reveal delay={100}>
              <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]">
                Expert Groundworks,<br />
                Drainage & Landscaping<br />
                In <span className="text-[#F28C00]">Edinburgh & Central Scotland</span>
              </h1>
            </Reveal>
            <Reveal delay={200}>
              <p className="text-white/85 text-base sm:text-lg leading-relaxed mt-8 max-w-xl">
                Trusted by developers, homeowners, and commercial clients across the Central Belt, City Civils delivers premium groundworks, expert drainage solutions, and professional landscaping — done right the first time.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="flex flex-wrap items-center gap-4 mt-10">
                <Link to="/contact" className="cc-btn-primary" data-testid="hero-cta-quote">
                  Get A Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="#services" className="cc-btn-outline" data-testid="hero-cta-services">Our Services</a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FEATURE CARDS */}
      <section className="bg-[#0B0B0B] py-20 md:py-24 border-y border-[#181818]" id="projects-anchor">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6 cc-stagger">
          {FEATURE_CARDS.map((c) => (
            <Reveal key={c.title} className="group bg-[#111111] border border-[#181818] hover:border-[#F28C00]/60 p-8 transition-colors">
              <div className="w-12 h-12 inline-flex items-center justify-center border border-[#F28C00]/60 text-[#F28C00] mb-6 group-hover:bg-[#F28C00] group-hover:text-white transition-colors">
                <c.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold uppercase text-white text-xl tracking-tight mb-3">{c.title}</h3>
              <p className="text-[#9A9A9A] text-sm leading-relaxed">{c.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES (white) */}
      <section id="services" className="bg-white py-24 md:py-32" data-testid="home-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Reveal>
              <p className="inline-flex items-center gap-3 text-[#F28C00] font-display font-bold uppercase tracking-[0.25em] text-xs mb-5">
                <span className="block w-8 h-[2px] bg-[#F28C00]" /> What We Do
              </p>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl text-[#0B0B0B] leading-[1.05]">
                Comprehensive Construction & Landscaping Services
              </h2>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 cc-stagger">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || Wrench;
              return (
                <Reveal key={s.title} className="group bg-white border border-gray-200 p-8 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                  <div className="w-12 h-12 inline-flex items-center justify-center border border-[#F28C00] text-[#F28C00] mb-6 group-hover:bg-[#F28C00] group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display font-bold uppercase text-[#0B0B0B] text-xl tracking-tight mb-3">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">{s.description}</p>
                  <Link to={s.to} className="inline-flex items-center gap-2 text-[#F28C00] font-display font-bold uppercase text-xs tracking-[0.18em] hover:gap-3 transition-all" data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    Learn More <ArrowRight className="w-4 h-4" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#050505] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal variant="img" className="relative order-2 lg:order-1">
            <div className="cc-hero-offset">
              <img src={WHY_IMAGE} alt="Premium night-lit patio installation by City Civils" className="w-full aspect-[4/5] object-cover" />
            </div>
          </Reveal>

          <div className="order-1 lg:order-2">
            <Reveal>
              <p className="cc-eyebrow mb-6">Why Choose Us</p>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]">
                An Exceptional Quality<br />That Can't Be Beaten
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <ul className="mt-10 space-y-5">
                {["Drainage Done Right", "Professional & Organised", "No Cowboys Just Craftsmanship"].map((t) => (
                  <li key={t} className="flex items-start gap-4">
                    <span className="mt-1.5 w-2.5 h-2.5 bg-[#F28C00] shrink-0" />
                    <span className="text-white font-display font-bold uppercase tracking-wider text-base">{t}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-10">
                <Link to="/contact" className="cc-btn-primary" data-testid="why-cta-quote">
                  Get A Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      <section className="bg-[#0B0B0B] py-24 md:py-32 border-y border-[#181818]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Reveal><p className="cc-eyebrow justify-center mb-6">Testimonials</p></Reveal>
            <Reveal delay={100}>
              <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]">
                What Our Customers Say
              </h2>
            </Reveal>
          </div>

          <div className="mb-14">
            <GoogleReviewsPanel rating={5.0} count={9} />
          </div>

          <div className="grid md:grid-cols-3 gap-6 cc-stagger">
            {TESTIMONIALS.slice(0, 3).map((t, i) => (
              <Reveal key={t.name} className="bg-[#111111] border-t-[3px] border-[#F28C00] p-8 relative">
                <Quote className="w-10 h-10 text-[#F28C00] mb-6" />
                <p className="text-[#9A9A9A] text-sm leading-relaxed mb-8">"{t.text}"</p>
                <div className="flex items-center gap-4 pt-6 border-t border-[#181818]">
                  <img src={t.image} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="text-white font-display font-bold uppercase tracking-wider text-sm">{t.name}</div>
                    <Stars className="mt-1.5" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="text-center mt-14">
            <Link to="/testimonials" className="cc-btn-outline" data-testid="testimonials-view-all">
              View All Testimonials <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
